import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetHabitById,
  useUpdateHabit,
  getGetUserHabitsQueryKey,
} from "../../generated/hook";
import { Habit, UpdateHabitInputClassFrequency } from "../../generated/model";
import useAuth from "../../hooks/useAuth";

const HabitEditPage: React.FC = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [frequency, setFrequency] =
    useState<UpdateHabitInputClassFrequency>("daily");
  const [target, setTarget] = useState(1);

  const { user } = useAuth();
  const navigate = useNavigate();
  const { habitId } = useParams<{ habitId: string }>();
  const queryClient = useQueryClient();

  // Fetch habit data - call hooks before any conditional returns
  const {
    data: habitResponse,
    isLoading,
    isError,
    error,
  } = useGetHabitById(habitId || "");

  // Update habit mutation
  const updateHabitMutation = useUpdateHabit({
    mutation: {
      onSuccess: () => {
        // Invalidate habits query to refresh the list
        queryClient.invalidateQueries({
          queryKey: getGetUserHabitsQueryKey(),
        });
        // Navigate back to habits page
        navigate("/habits");
      },
      onError: (error) => {
        console.error("Failed to update habit:", error);
      },
    },
  });

  // Initialize form when habit data is loaded
  useEffect(() => {
    if (habitResponse && Array.isArray(habitResponse)) {
      // Handle the case where the response is an array (shouldn't happen for single habit)
      return;
    }

    if (habitResponse && "id" in habitResponse) {
      const habit = habitResponse as Habit;
      setName(habit.name);
      setDescription(habit.description || "");
      setFrequency(habit.frequency as UpdateHabitInputClassFrequency);
      setTarget(habit.target);
    }
  }, [habitResponse]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!habitId) {
      console.error("No habit ID provided");
      return;
    }

    const updateData = {
      name,
      description,
      frequency,
      target,
    };

    updateHabitMutation.mutate({
      id: habitId,
      data: updateData,
    });
  };

  if (isLoading || !user || !habitId) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            {!user
              ? "Authenticating..."
              : !habitId
              ? "Loading habit..."
              : "Loading habit data..."}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">
            Failed to load habit:{" "}
            {error instanceof Error ? error.message : "Unknown error"}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate("/habits")}
            sx={{ mt: 2 }}
          >
            Back to Habits
          </Button>
        </Box>
      </Container>
    );
  }

  // Check if habit data is valid
  const habit = habitResponse as Habit;
  if (!habit || !("id" in habit)) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">Habit not found or invalid data</Alert>
          <Button
            variant="contained"
            onClick={() => navigate("/habits")}
            sx={{ mt: 2 }}
          >
            Back to Habits
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Edit Habit
        </Typography>

        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <form onSubmit={handleSubmit}>
            <TextField
              label="Habit Name"
              fullWidth
              margin="normal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              margin="normal"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <TextField
              select
              label="Frequency"
              fullWidth
              margin="normal"
              value={frequency}
              onChange={(e) =>
                setFrequency(e.target.value as UpdateHabitInputClassFrequency)
              }
              required
              SelectProps={{ native: true }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </TextField>

            <TextField
              label="Target (times per period)"
              type="number"
              fullWidth
              margin="normal"
              value={target}
              onChange={(e) => setTarget(Number(e.target.value))}
              inputProps={{ min: 1 }}
              required
            />

            <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={updateHabitMutation.isPending}
              >
                {updateHabitMutation.isPending ? (
                  <CircularProgress size={24} />
                ) : (
                  "Save Changes"
                )}
              </Button>

              <Button variant="outlined" onClick={() => navigate("/habits")}>
                Cancel
              </Button>
            </Box>

            {updateHabitMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Failed to update habit:{" "}
                {updateHabitMutation.error
                  ? updateHabitMutation.error.message
                  : "Unknown error"}
              </Alert>
            )}

            {updateHabitMutation.isSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Habit updated successfully!
              </Alert>
            )}
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default HabitEditPage;
